import type { IntlShape } from 'react-intl'
import { PublicHolidaysRepository } from '@vacationtracker/shared/common/date-holidays-repository'
import type { IDateHolidaysHoliday } from '@vacationtracker/shared/common/date-holidays-repository'
import type { IPlace } from '@vacationtracker/shared/data/countries'
import { holidayDay } from '@vacationtracker/shared/functions/holiday-tags'

import { HolidayRow, rowYear } from './logic'

export type LibraryDate =
  | { kind: 'date'; month: number; day: number }
  | { kind: 'nthWeekday'; count: number; weekday: number; month: number }
  | { kind: 'relative'; count: number; weekday: number; direction: 'before' | 'after'; anchor: LibraryDate }
  | { kind: 'easter'; orthodox: boolean; offset: number }
  | { kind: 'julian'; month: number; day: number }
  | { kind: 'calendar'; calendar: 'islamic' | 'hebrew' | 'jalaali'; day: number; month: string }
  | { kind: 'lunar'; calendar: 'chinese' | 'korean' | 'vietnamese'; month: number; day: number }

export interface LibraryMove {
  /** 0 = Sunday. */
  on: number[]
  direction: 'next' | 'previous'
  to: number
}

export interface LibraryRule {
  date: LibraryDate
  /** The holiday only exists as the stand-in for `date` when that falls on a day in `moves`. */
  substitute: boolean
  moves: LibraryMove[]
  /** `and if …`: the date stays a holiday and the moved day is added as a substitute. */
  movesAddDay: boolean
  time?: string
  since?: number
  priorTo?: number
}

const WEEKDAY = 'sunday|monday|tuesday|wednesday|thursday|friday|saturday'
const WEEKDAYS = WEEKDAY.split('|')
const MONTHS = 'january|february|march|april|may|june|july|august|september|october|november|december'.split('|')
const CALENDAR_MONTHS = {
  islamic: 'Muharram|Safar|Rabi al-awwal|Rabi al-thani|Jumada al-awwal|Jumada al-thani|Rajab|Shaban|Ramadan|Shawwal|Dhu al-Qidah|Dhu al-Hijjah',
  hebrew: 'Nisan|Iyyar|Sivan|Tamuz|Av|Elul|Tishrei|Cheshvan|Kislev|Tevet|Shvat|AdarII|Adar',
  jalaali: 'Farvardin|Ordibehesht|Khordad|Tir|Mordad|Shahrivar|Mehr|Aban|Azar|Dey|Bahman|Esfand',
} as const

const weekdayOf = (word: string): number => WEEKDAYS.indexOf(word.toLowerCase())

type Parsed<T> = [T, string] | null

function parseDate(input: string): Parsed<LibraryDate> {
  let match: RegExpExecArray | null
  if ((match = /^julian 0?(\d{1,2})-0?(\d{1,2})/.exec(input))) {
    return [{ kind: 'julian', month: Number(match[1]), day: Number(match[2]) }, input.slice(match[0].length)]
  }
  // A full YYYY-MM-DD is a one-off date the library fixes per year, not a rule.
  if ((match = /^0?(\d{1,2})-0?(\d{1,2})(?![\d-])/.exec(input))) {
    return [{ kind: 'date', month: Number(match[1]), day: Number(match[2]) }, input.slice(match[0].length)]
  }
  if ((match = /^(easter|orthodox)(?: ([-+]?\d{1,3})(?: ?days?| ?d)?\b)?/.exec(input))) {
    return [{ kind: 'easter', orthodox: match[1] === 'orthodox', offset: Number(match[2] ?? 0) }, input.slice(match[0].length)]
  }
  for (const calendar of Object.keys(CALENDAR_MONTHS) as (keyof typeof CALENDAR_MONTHS)[]) {
    if ((match = new RegExp(`^0?(\\d{1,2}) (${CALENDAR_MONTHS[calendar]})(?![\\w-]| \\d)`).exec(input))) {
      return [{ kind: 'calendar', calendar, day: Number(match[1]), month: match[2].replace('AdarII', 'Adar II') }, input.slice(match[0].length)]
    }
  }
  // Leap months and cycle-year rules are left to the fallback.
  if ((match = /^(chinese|korean|vietnamese) 0?(\d{1,2})-0-0?(\d{1,2})(?![\d-]| solarterm)/.exec(input))) {
    return [{ kind: 'lunar', calendar: match[1] as 'chinese', month: Number(match[2]), day: Number(match[3]) }, input.slice(match[0].length)]
  }
  if ((match = new RegExp(`^(\\d)(?:st|nd|rd|th) (${WEEKDAY}) in (${MONTHS.join('|')})\\b`, 'i').exec(input))) {
    const count = Number(match[1])
    if (count === 0) return null
    return [{ kind: 'nthWeekday', count, weekday: weekdayOf(match[2]), month: MONTHS.indexOf(match[3].toLowerCase()) + 1 }, input.slice(match[0].length)]
  }
  if ((match = new RegExp(`^(?:(\\d{1,2})(?:st|nd|rd|th) )?(${WEEKDAY}) (before|after) `, 'i').exec(input))) {
    const anchor = parseDate(input.slice(match[0].length))
    if (!anchor) return null
    const direction = match[3] as 'before' | 'after'
    return [{ kind: 'relative', count: Number(match[1] ?? 1), weekday: weekdayOf(match[2]), direction, anchor: anchor[0] }, anchor[1]]
  }
  return null
}

const CLAUSES: { pattern: RegExp; apply: (rule: LibraryRule, match: RegExpExecArray) => void }[] = [
  { pattern: /^#\d+\b/, apply: () => undefined },
  { pattern: /^and\b/, apply: (rule) => (rule.movesAddDay = true) },
  {
    pattern: new RegExp(`^if ((?:(?:${WEEKDAY}),? ?)+) then (next|previous) (${WEEKDAY})\\b`, 'i'),
    apply: (rule, match) => {
      const on = match[1]
        .split(/[,\s]+/)
        .filter(Boolean)
        .map(weekdayOf)
      rule.moves.push({ on, direction: match[2].toLowerCase() as 'next', to: weekdayOf(match[3]) })
    },
  },
  // The start time differs on some weekdays, so no single time is true.
  { pattern: new RegExp(`^if ((?:(?:${WEEKDAY}),? ?)+) then \\d{2}:\\d{2}\\b`, 'i'), apply: (rule) => (rule.time = undefined) },
  { pattern: /^(\d{2}:\d{2})\b/, apply: (rule, match) => (rule.time = match[1]) },
  { pattern: /^P(?:\d+D)?(?:T(?:\d+H)?(?:\d+M)?)?\b/, apply: () => undefined },
  // A month or day after the year ("since 2026-10") is left to the fallback: the year alone would be wrong for part of it.
  { pattern: /^since (\d{4})(?![\d-])/, apply: (rule, match) => (rule.since = Number(match[1])) },
  { pattern: /^prior to (\d{4})(?![\d-])/, apply: (rule, match) => (rule.priorTo = Number(match[1])) },
]

/** The date-holidays rule as data, or null for any part this does not describe, so nothing is half-explained. */
export function parseLibraryRule(rule: string): LibraryRule | null {
  const substitute = rule.startsWith('substitutes ')
  const parsed = parseDate(substitute ? rule.slice('substitutes '.length) : rule)
  if (!parsed) return null
  const result: LibraryRule = { date: parsed[0], substitute, moves: [], movesAddDay: false }
  let rest = parsed[1].trim()
  while (rest) {
    const clause = CLAUSES.map(({ pattern, apply }) => ({ match: pattern.exec(rest), apply })).find(({ match }) => match)
    if (!clause?.match) return null
    clause.apply(result, clause.match)
    rest = rest.slice(clause.match[0].length).trim()
  }
  // A substitute day without the weekday it stands in for has nothing to say.
  if (substitute && !result.moves.length) return null
  return result
}

/** date-holidays flags a day added by an `and if …` or `substitutes` rule. */
type LibraryHoliday = IDateHolidaysHoliday & { substitute?: boolean }

export interface LibraryMatch {
  rule: string
  /** The row is the added day, not the holiday's own date. */
  substitute: boolean
  /** public, bank, school, optional or observance. */
  type: string
}

const holidaysCache = new Map<string, LibraryHoliday[]>()

function libraryHolidays(place: IPlace, locale: string, year: number): LibraryHoliday[] {
  const key = `${place.countryCode}|${place.subdivisionCode ?? ''}|${locale}|${year}`
  let holidays = holidaysCache.get(key)
  if (!holidays) {
    holidays = new PublicHolidaysRepository(locale).getHolidaysForCountry(year, place.countryCode, place.subdivisionCode ?? undefined) || []
    holidaysCache.set(key, holidays)
  }
  return holidays
}

const statesCache = new Map<string, string[]>()

function countryStates(countryCode: string): string[] {
  let states = statesCache.get(countryCode)
  if (!states) {
    states = new PublicHolidaysRepository().getStates(countryCode)
    statesCache.set(countryCode, states)
  }
  return states
}

/**
 * The library's rule for an imported row, by its library name in the place the location's holidays are imported for,
 * preferring the row's own day.
 *
 * A library update can rename a holiday or move it from the national list to the regions. Until the next yearly
 * import stores the new name, the row falls back to the one library holiday on its own day, or to another region of
 * the country that still has the name on that day.
 */
export function libraryRuleOf(row: HolidayRow, place?: IPlace | null): LibraryMatch | undefined {
  if (row.type !== 'LIB' || !row.thirdPartyName || !place?.countryCode) return undefined
  const day = holidayDay(row.date)
  const locale = row.thirdPartyLocale || 'en'
  const year = rowYear(row)
  const holidays = libraryHolidays(place, locale, year)
  const named = holidays.filter((holiday) => holiday.name === row.thirdPartyName)
  const onlyOnDay = () => {
    const onDay = holidays.filter((holiday) => holidayDay(holiday.date) === day)
    return onDay.length === 1 ? onDay[0] : undefined
  }
  const regional = () => {
    for (const state of countryStates(place.countryCode)) {
      const match = libraryHolidays({ ...place, subdivisionCode: state }, locale, year).find((holiday) => holiday.name === row.thirdPartyName && holidayDay(holiday.date) === day)
      if (match) return match
    }
    return undefined
  }
  const match = named.find((holiday) => holiday.date.slice(0, 10) === day) ?? named[0] ?? onlyOnDay() ?? regional()
  return match && { rule: match.rule, substitute: Boolean(match.substitute), type: match.type }
}

export type LibraryRules = Map<HolidayRow, LibraryMatch>

export const libraryRulesOf = (rows: HolidayRow[], place?: IPlace | null): LibraryRules =>
  new Map(
    rows.flatMap((row) => {
      const match = libraryRuleOf(row, place)
      return match ? [[row, match] as const] : []
    })
  )

type Format = Pick<IntlShape, 'formatMessage' | 'formatDate' | 'formatList'>

const weekdayName = (intl: Format, weekday: number): string => intl.formatDate(Date.UTC(2023, 0, 1 + weekday), { weekday: 'long', timeZone: 'UTC' })
const monthName = (intl: Format, month: number): string => intl.formatDate(Date.UTC(2023, month - 1, 1), { month: 'long', timeZone: 'UTC' })
const dayOfMonth = (intl: Format, month: number, day: number): string => intl.formatDate(Date.UTC(2024, month - 1, day), { month: 'long', day: 'numeric', timeZone: 'UTC' })

function describeDate(intl: Format, date: LibraryDate): string {
  const { formatMessage } = intl
  switch (date.kind) {
    case 'date':
      return dayOfMonth(intl, date.month, date.day)
    case 'nthWeekday':
      return formatMessage({ id: 'holidaysTab.libraryRule.nthWeekday' }, { count: date.count, weekday: weekdayName(intl, date.weekday), month: monthName(intl, date.month) })
    case 'relative':
      return formatMessage(
        { id: date.direction === 'before' ? 'holidaysTab.libraryRule.weekdayBefore' : 'holidaysTab.libraryRule.weekdayAfter' },
        { count: date.count, weekday: weekdayName(intl, date.weekday), anchor: describeDate(intl, date.anchor) }
      )
    case 'easter': {
      const easter = formatMessage({ id: 'holidaysTab.libraryRule.easter' }, { orthodox: date.orthodox ? 'yes' : 'no' })
      if (!date.offset) return easter
      return formatMessage({ id: date.offset < 0 ? 'holidaysTab.libraryRule.daysBefore' : 'holidaysTab.libraryRule.daysAfter' }, { days: Math.abs(date.offset), anchor: easter })
    }
    case 'julian':
      return formatMessage({ id: 'holidaysTab.libraryRule.julian' }, { date: dayOfMonth(intl, date.month, date.day) })
    case 'calendar':
      return formatMessage({ id: 'holidaysTab.libraryRule.calendar' }, { day: date.day, month: date.month, calendar: date.calendar })
    case 'lunar':
      return formatMessage({ id: 'holidaysTab.libraryRule.lunar' }, { day: date.day, month: date.month, calendar: date.calendar })
  }
}

export interface LibraryRuleText {
  /** The date alone: "2nd Monday of June". */
  short: string
  /** With the conditions in the rule: weekend moves or added days, start time, the years it holds. */
  full: string
}

export function describeLibraryRule(intl: Format, rule: LibraryRule): LibraryRuleText {
  const { formatMessage } = intl
  const date = describeDate(intl, rule.date)
  const short = rule.substitute ? formatMessage({ id: 'holidaysTab.libraryRule.substitute' }, { rule: date }) : date
  const details = [
    ...rule.moves.map((move) =>
      formatMessage(
        { id: rule.movesAddDay ? 'holidaysTab.libraryRule.addDay' : 'holidaysTab.libraryRule.move' },
        {
          days: intl.formatList(
            move.on.map((weekday) => weekdayName(intl, weekday)),
            { type: 'disjunction' }
          ),
          direction: move.direction,
          weekday: weekdayName(intl, move.to),
        }
      )
    ),
    rule.time && formatMessage({ id: 'holidaysTab.libraryRule.time' }, { time: rule.time }),
    rule.since && formatMessage({ id: 'holidaysTab.libraryRule.since' }, { year: String(rule.since) }),
    rule.priorTo && formatMessage({ id: 'holidaysTab.libraryRule.priorTo' }, { year: String(rule.priorTo) }),
  ].filter(Boolean)
  return { short, full: [short, ...details].join('; ') }
}

export interface LibraryRuleSummary extends LibraryRuleText {
  /** The hover and popover line: "Every year on …", or the substitute's own reading. */
  summary: string
}

/** The library's rule for an imported row in words, or undefined to keep "Library date". */
export function libraryRuleText(intl: Format, match?: LibraryMatch): LibraryRuleSummary | undefined {
  const parsed = match ? parseLibraryRule(match.rule) : null
  if (!parsed) return undefined
  // A row the rule added on another weekday is the substitute, whatever date the rule names.
  const text = describeLibraryRule(intl, { ...parsed, substitute: parsed.substitute || Boolean(match?.substitute) })
  const summary = match?.substitute || parsed.substitute ? text.full : intl.formatMessage({ id: 'holidaysTab.recurrence.summaryFixed' }, { date: text.full })
  return { ...text, summary }
}
